import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Helmet } from "react-helmet"
import NavigationBar from "../../components/ui/NavigationBar"
import ControlPlaneSidebar from "./components/ControlPlaneSidebar"
import OverviewPanel from "./components/OverviewPanel"
import OrganizationsPanel from "./components/OrganizationsPanel"
import EventExplorer from "./components/EventExplorer"
import PeoplePanel from "./components/PeoplePanel"
import ComingSoonPanel from "../organization-dashboard/components/ComingSoonPanel"
import { authHelpers } from "../../firebaseClient"
import { SidebarProvider, SidebarInset, SidebarTrigger } from "../../components/ui/sidebar"
import { checkIsPlatformAdmin } from "../../utils/platformAdminHelpers"

const TAB_LABELS = {
  overview: "Mission Control",
  organizations: "Organizations",
  people: "People",
  learning: "Learning",
  credentials: "Credentials & POL",
  economy: "Economy",
  operations: "Operations",
  security: "Security & Governance",
}

const ControlPlane = () => {
  const navigate = useNavigate()

    const [loading, setLoading] = useState(true)
  const [adminUid, setAdminUid] = useState(null)
  const [activeTab, setActiveTab] = useState("overview")
  const [jumpToOrgId, setJumpToOrgId] = useState(null)
  const [jumpToUid, setJumpToUid] = useState(null)

  const navigateToOrg = (orgId) => {
    setJumpToOrgId(orgId)
    setActiveTab("organizations")
  }

  useEffect(() => {
    const loadControlPlane = async () => {
      const { session, error: sessionError } = await authHelpers.getSession()

      if (sessionError || !session) {
        navigate("/authentication-portal", { replace: true })
        return
      }

      const { isAdmin } = await checkIsPlatformAdmin(session.user.uid)

      if (!isAdmin) {
        navigate("/user-dashboard", { replace: true })
        return
      }

      setAdminUid(session.user.uid)
      setLoading(false)
    }

    loadControlPlane()
  }, [navigate])

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading control plane...</span>
        </div>
      </div>
    )
  }

  const renderActiveTab = () => {
    switch (activeTab) {
                  case "overview":
        return <OverviewPanel />
      case "organizations":
        return <OrganizationsPanel initialOrgId={jumpToOrgId} onConsumeInitialOrgId={() => setJumpToOrgId(null)} />
      case "operations":
        return <EventExplorer onNavigateToOrg={navigateToOrg} />
      case "people":
        return <PeoplePanel initialUid={jumpToUid} onConsumeInitialUid={() => setJumpToUid(null)} />
      default:
        return <ComingSoonPanel label={TAB_LABELS?.[activeTab] || "This section"} />
    }
  }

  return (
    <>
      <Helmet>
        <title>Control Plane - Nesolearn</title>
      </Helmet>

      <div id="control-plane-theme-root" className="min-h-screen bg-muted dark:bg-background overflow-x-hidden">
        <NavigationBar themed />

        <SidebarProvider className="items-start pt-24 sm:pt-28">
          <ControlPlaneSidebar activeTab={activeTab} onTabChange={setActiveTab} />

          <SidebarInset className="mx-auto w-full max-w-6xl p-4 md:p-6 bg-transparent min-w-0">
            <div className="flex md:hidden mb-4">
              <SidebarTrigger className="border border-border bg-card shadow-sm rounded-lg size-9" />
            </div>
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-foreground">{TAB_LABELS?.[activeTab]}</h1>
              {activeTab === "overview" && (
                <p className="text-muted-foreground mt-1">
                  What's happening across the entire Nesolearn ecosystem.
                </p>
              )}
            </div>

            {renderActiveTab()}
          </SidebarInset>
        </SidebarProvider>
      </div>
    </>
  )
}

export default ControlPlane